import { Box, Button, Divider, H3, H4, Icon } from '@deity/falcon-ui';
import { Skeleton } from '@material-ui/lab';
import { BaseHelmet, EntityDetailsHeader, MappingSummary } from 'components';
import EditMappingModal from 'components/Mappings/EditMappingModal';
import { APOLLO_BASE_OPTIONS, CACHE_FIRST } from 'contracts/constants';
import type React from 'react';
import { useState } from 'react';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';
import { useGetEntityUsageInfoQuery, useGetMappingQuery, useGetModelQuery } from 'services/graphql/generated/hooks';

type MappingOverViewProps = {
  params: any;
  isEditable?: boolean;
};

export const MappingOverview: React.FC<MappingOverViewProps> = ({ params, isEditable }) => {
  const { pathname } = useLocation();
  const { url } = useRouteMatch();
  const [mappingModalOpen, setMappingModalOpen] = useState<boolean>(false);
  const {
    loading: loadingMapping,
    data: mappingData,
    refetch
  } = useGetMappingQuery({
    variables: {
      uuid: params.entityId,
      packageVersionUuid: params.versionUuid
    },
    skip: !params.entityId,
    ...CACHE_FIRST
  });
  const {
    loading: loadingSourceModel,
    data: sourceModelData,
    refetch: refetchSource
  } = useGetModelQuery({
    variables: {
      packageVersionUuid: params.versionUuid,
      uuid: mappingData?.getMapping?.schema._meta?.source,
      expand: true
    },
    skip: !mappingData?.getMapping?.schema._meta?.source,
    ...CACHE_FIRST
  });
  const {
    loading: loadingTargetModel,
    data: targetModelData,
    refetch: refetchTarget
  } = useGetModelQuery({
    variables: {
      uuid: mappingData?.getMapping?.schema._meta?.target,
      packageVersionUuid: params.versionUuid,
      expand: true
    },
    skip: !mappingData?.getMapping?.schema._meta?.target,
    ...CACHE_FIRST
  });

  const {
    loading: loadingInfo,
    data: infoData,
    refetch: refetchInfo
  } = useGetEntityUsageInfoQuery({
    variables: {
      versionUuid: params.versionUuid,
      entityType: 'mappings',
      entityKey: params.entityId
    },
    skip: !(params.versionUuid && params.entityId),
    ...APOLLO_BASE_OPTIONS
  });

  return (
    <>
      <BaseHelmet page={mappingData?.getMapping?.name ?? 'Mapping'} />
      <EntityDetailsHeader
        isLoading={loadingMapping}
        name={mappingData?.getMapping?.name}
        description={mappingData?.getMapping?.description}
        onModalOpen={setMappingModalOpen}
        isEditable={isEditable}
      />
      <Divider borderColor="secondary" />
      <Box p="xxl">
        <H4 fontWeight="bold" mb="md" fontSize="lg">
          Summary
        </H4>
        {loadingMapping || loadingSourceModel || loadingTargetModel ? (
          <Skeleton width="100%" height="200px" />
        ) : (
          <MappingSummary
            mappingData={mappingData}
            sourceModelData={sourceModelData}
            targetModelData={targetModelData}
          />
        )}

        <Box mt="xxxl">
          <Link
            to={`${url.slice(0, url.lastIndexOf('/'))}/editor/${params.entityId}`}
            style={{ textDecoration: 'none' }}
          >
            <Button variant="border">
              <Icon src="edit" mr="xs" stroke="transparent" />
              Mapping editor
            </Button>
          </Link>
        </Box>
      </Box>
      <Divider />
      <Box p="xxl">
        {loadingInfo && (
          <Box>
            <H4 fontSize="xl" fontWeight="bold" mr="xs">
              Used in {infoData?.getEntityUsageInfo.flows.length} flow
              {infoData?.getEntityUsageInfo.flows.length === 1 ? '' : 's'}.
            </H4>
            <H3 color="secondary">This Mapping is used in the following Flows.</H3>

            <Box borderRadius="small" p="xl" bg="lightGrey" mt="md">
              {infoData?.getEntityUsageInfo.flows.map(item => (
                <H3 key={item.uuid} color="secondary" fontSize="lg">
                  Flow : <Link to={`../flows/${item.uuid}`}>{item.name}</Link>
                </H3>
              ))}
            </Box>
          </Box>
        )}
      </Box>
      <EditMappingModal
        versionUuid={params.versionUuid}
        isOpen={mappingModalOpen}
        editMapping={mappingData}
        setOpenState={state => setMappingModalOpen(state)}
        onPackageCreated={() => {
          if (refetch) {
            refetch();
          }
        }}
        id="edit-mapping-modal"
      />
    </>
  );
};
